// Inc E — fullscreen lightbox for hotspot gallery images. Module-level
// singleton mounted lazily on first open. Esc/Arrows are handled in the
// CAPTURE phase + stopPropagation so the global popup-close shortcut and the
// popup's own arrow handler never fire while the lightbox is open. Restores
// focus to the opener on close (mirrors the hotspot-popup focus handling).

import type { HotspotMedia } from "../../api/types.js";
import { t } from "../../i18n/init.js";

interface LightboxState {
	media: readonly HotspotMedia[];
	index: number;
}

let overlay: HTMLElement | null = null;
let imgEl: HTMLImageElement | null = null;
let captionEl: HTMLElement | null = null;
let counterEl: HTMLElement | null = null;
let closeBtn: HTMLButtonElement | null = null;
let prevBtn: HTMLButtonElement | null = null;
let nextBtn: HTMLButtonElement | null = null;
let state: LightboxState | null = null;
let lastFocused: HTMLElement | null = null;

function clampIndex(i: number, len: number): number {
	if (len <= 0) return 0;
	return ((i % len) + len) % len; // cyclic
}

function renderCurrent(): void {
	if (
		state === null ||
		imgEl === null ||
		captionEl === null ||
		counterEl === null
	)
		return;
	const m = state.media[state.index];
	if (m === undefined) return;
	imgEl.src = m.url;
	imgEl.alt =
		m.caption !== null && m.caption.length > 0
			? m.caption
			: t("viewer.hotspot.gallery_image_alt", { n: state.index + 1 });
	if (m.caption !== null && m.caption.length > 0) {
		captionEl.textContent = m.caption;
		captionEl.style.display = "block";
	} else {
		captionEl.textContent = "";
		captionEl.style.display = "none";
	}
	counterEl.textContent = t("viewer.hotspot.gallery_counter", {
		current: state.index + 1,
		total: state.media.length,
	});
	const multi = state.media.length > 1;
	if (prevBtn !== null) prevBtn.style.display = multi ? "flex" : "none";
	if (nextBtn !== null) nextBtn.style.display = multi ? "flex" : "none";
	if (counterEl !== null) counterEl.style.display = multi ? "block" : "none";
}

function step(delta: number): void {
	if (state === null) return;
	state = {
		media: state.media,
		index: clampIndex(state.index + delta, state.media.length),
	};
	renderCurrent();
}

function focusableEls(): HTMLElement[] {
	return [closeBtn, prevBtn, nextBtn].filter(
		(el): el is HTMLButtonElement => el !== null && el.style.display !== "none",
	);
}

function onKeydownCapture(ev: KeyboardEvent): void {
	if (state === null) return; // closed → let events flow normally
	if (ev.key === "Escape") {
		ev.preventDefault();
		ev.stopPropagation();
		closeHotspotLightbox();
	} else if (ev.key === "ArrowLeft") {
		ev.preventDefault();
		ev.stopPropagation();
		step(-1);
	} else if (ev.key === "ArrowRight") {
		ev.preventDefault();
		ev.stopPropagation();
		step(1);
	} else if (ev.key === "Tab") {
		// Basic focus trap: keep focus within the overlay controls.
		const els = focusableEls();
		if (els.length === 0) return;
		const first = els[0];
		const last = els[els.length - 1];
		const active = document.activeElement as HTMLElement | null;
		if (ev.shiftKey && active === first) {
			ev.preventDefault();
			last?.focus();
		} else if (!ev.shiftKey && active === last) {
			ev.preventDefault();
			first?.focus();
		}
	}
}

function ensureMounted(): void {
	if (overlay !== null) return;

	overlay = document.createElement("div");
	overlay.classList.add("viewer-ui-interactive");
	overlay.setAttribute("role", "dialog");
	overlay.setAttribute("aria-modal", "true");
	overlay.style.cssText = [
		"position:fixed",
		"inset:0",
		"z-index:60",
		"display:none",
		"align-items:center",
		"justify-content:center",
		"background:rgba(0,0,0,0.92)",
		"pointer-events:auto",
	].join(";");

	// Click on the backdrop (not the image) closes.
	overlay.addEventListener("click", (ev) => {
		if (ev.target === overlay) closeHotspotLightbox();
	});

	imgEl = document.createElement("img");
	imgEl.decoding = "async";
	imgEl.style.cssText =
		"max-width:92vw;max-height:82vh;object-fit:contain;border-radius:0.5rem;box-shadow:0 20px 60px rgba(0,0,0,0.6)";
	overlay.appendChild(imgEl);

	captionEl = document.createElement("div");
	captionEl.style.cssText =
		"position:absolute;bottom:2.5rem;left:50%;transform:translateX(-50%);max-width:80vw;color:#f4f4f5;font-family:system-ui,sans-serif;font-size:0.8125rem;text-align:center;background:rgba(0,0,0,0.5);padding:0.375rem 0.75rem;border-radius:0.5rem";
	overlay.appendChild(captionEl);

	counterEl = document.createElement("div");
	counterEl.style.cssText =
		"position:absolute;top:1rem;left:50%;transform:translateX(-50%);color:#d4d4d8;font-family:ui-monospace,monospace;font-size:0.75rem";
	overlay.appendChild(counterEl);

	const navStyle =
		"position:absolute;top:50%;transform:translateY(-50%);width:2.75rem;height:2.75rem;border-radius:9999px;background:rgba(0,0,0,0.5);color:#fff;border:0;cursor:pointer;font-size:1.5rem;line-height:1;align-items:center;justify-content:center";
	prevBtn = document.createElement("button");
	prevBtn.type = "button";
	prevBtn.classList.add("viewer-ui-tap-target");
	prevBtn.setAttribute("aria-label", t("viewer.hotspot.gallery_prev_label"));
	prevBtn.textContent = "‹";
	prevBtn.style.cssText = `${navStyle};left:1rem;display:flex`;
	prevBtn.addEventListener("click", () => step(-1));
	overlay.appendChild(prevBtn);

	nextBtn = document.createElement("button");
	nextBtn.type = "button";
	nextBtn.classList.add("viewer-ui-tap-target");
	nextBtn.setAttribute("aria-label", t("viewer.hotspot.gallery_next_label"));
	nextBtn.textContent = "›";
	nextBtn.style.cssText = `${navStyle};right:1rem;display:flex`;
	nextBtn.addEventListener("click", () => step(1));
	overlay.appendChild(nextBtn);

	closeBtn = document.createElement("button");
	closeBtn.type = "button";
	closeBtn.classList.add("viewer-ui-tap-target");
	closeBtn.setAttribute("aria-label", t("viewer.hotspot.lightbox_close_label"));
	closeBtn.textContent = "×";
	closeBtn.style.cssText =
		"position:absolute;top:1rem;right:1rem;width:2.25rem;height:2.25rem;border-radius:9999px;background:rgba(0,0,0,0.5);color:#fff;border:0;cursor:pointer;font-size:1.25rem;display:flex;align-items:center;justify-content:center";
	closeBtn.addEventListener("click", () => closeHotspotLightbox());
	overlay.appendChild(closeBtn);

	document.body.appendChild(overlay);
	// Capture phase: intercept Esc/Arrows before popup + global shortcuts.
	document.addEventListener("keydown", onKeydownCapture, true);
}

export function openHotspotLightbox(
	media: readonly HotspotMedia[],
	startIndex: number,
): void {
	if (media.length === 0) return;
	ensureMounted();
	if (overlay === null) return;
	lastFocused = (document.activeElement as HTMLElement | null) ?? null;
	state = { media, index: clampIndex(startIndex, media.length) };
	renderCurrent();
	overlay.style.display = "flex";
	try {
		closeBtn?.focus();
	} catch {
		/* ignore */
	}
}

export function closeHotspotLightbox(): void {
	if (overlay === null || state === null) return;
	overlay.style.display = "none";
	state = null;
	if (imgEl !== null) imgEl.src = "";
	const restore = lastFocused;
	lastFocused = null;
	if (restore !== null && document.contains(restore)) {
		try {
			restore.focus();
		} catch {
			/* ignore */
		}
	}
}

export function isLightboxOpen(): boolean {
	return state !== null;
}
